import TableCRUDLayout, {
  TableCRUDLayoutHandlers
} from 'components/shared/CRUD/TableCRUDLayout';
import useGetTravelArrangements from './hooks/useGetTravelArrangements';
import { useGetColumns } from './hooks/useColumns';
import { t } from 'i18next';
import CreateEditForm from './components/CreateEditForm';
import useRemoveTravelArrangements from './hooks/useRemoveTravelArrangements';
import { Box } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { useState } from 'react';
import ConfirmationDialog from 'components/shared/ConfirmationDialog';
import {
  ExtendedTravelArrangementsData,
  TravelArrangementsProps
} from './types';
import LoadingOverlay from 'components/shared/LoadingOverlay';

export default function TravelArrangements({
  employeeId
}: TravelArrangementsProps) {
  const columns = useGetColumns();
  const { data: rows, isFetching } = useGetTravelArrangements(employeeId);
  const { mutate: handleDelete } = useRemoveTravelArrangements();
  const [isCopy, setIsCopy] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [rowToDelete, setRowToDelete] =
    useState<ExtendedTravelArrangementsData | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Add uniqueId to each row, endpoint does not return uniqueId's
  const uniqueRows =
    rows &&
    rows.map((row) => {
      const uniqueId = uuidv4();
      return { ...row, uniqueId };
    });

  const onCopy = (
    id: string | number,
    row: ExtendedTravelArrangementsData,
    props: TableCRUDLayoutHandlers
  ) => {
    if (!row) return;
    setIsCopy(true);
    if (props?.handleEditClick) props.handleEditClick(id, row);
  };

  const clearEdit = () => {
    setIsCopy(false);
  };

  const onDelete = (row: ExtendedTravelArrangementsData) => {
    if (!row) return;
    setRowToDelete(row);
    setShowConfirmationDialog(true);
  };

  function onDeleteRow(row: ExtendedTravelArrangementsData) {
    setIsDeleting(true);
    handleDelete({
      employeeId,
      empTrvArrangementId: row?.empTrvArrangementId as number
    });
  }

  const onDeleteConfirmation = (isConfirmed: boolean) => {
    if (isConfirmed && onDeleteRow) {
      if (rowToDelete) {
        onDeleteRow(rowToDelete);
        setRowToDelete(null);
        setIsDeleting(false);
      }
    }
    setShowConfirmationDialog(false);
  };

  return (
    <>
      <Box
        sx={{
          pt: 1
        }}
      >
        <TableCRUDLayout<ExtendedTravelArrangementsData>
          FormComponent={(props) => (
            <CreateEditForm {...props} employeeId={employeeId} />
          )}
          additionalFormProps={{ isCopy: isCopy }}
          getRowId={(uniqueRows) => uniqueRows?.uniqueId}
          rows={uniqueRows || []}
          columns={columns}
          loading={isFetching}
          shouldRenderToolBar={false}
          checkboxSelection={false}
          hideFooter={uniqueRows && uniqueRows?.length < 25}
          dynamicHeight={true}
          pageSizeOptions={uniqueRows && uniqueRows?.length < 25 ? [25] : []}
          pageSize={uniqueRows && 25}
          onClose={clearEdit}
          customActions={(id, row) => [
            {
              tooltipTitle: t('copy') as string,
              icon: 'copy',
              onClick: (id, row, props) =>
                onCopy(id, row as ExtendedTravelArrangementsData, props)
            },
            {
              tooltipTitle: t('delete') as string,
              icon: 'trash-can',
              onClick: () => onDelete(row as ExtendedTravelArrangementsData)
            }
          ]}
        />
      </Box>
      <LoadingOverlay loading={isDeleting} />
      {showConfirmationDialog && (
        <ConfirmationDialog
          handleAction={onDeleteConfirmation}
          cancelBtnText={t('no')}
          confirmBtnText={t('yes')}
          title={t('travelArrangements.confirmationTitle')}
          type="warning"
        >
          <p>{t('travelArrangements.confirmationBody')}</p>
        </ConfirmationDialog>
      )}
    </>
  );
}
